import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Container } from '../Container';

interface FooterLinkProps {
  to: string;
  children: React.ReactNode;
  external?: boolean;
}

const FooterLink: FC<FooterLinkProps> = ({ to, children, external }) => {
  const className = cn(
    'text-gray-400 hover:text-teal-400 transition-colors duration-200',
    'text-sm leading-relaxed'
  );

  if (external) {
    return (
      <a href={to} className={className} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }

  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
};

interface FooterSectionProps {
  title: string;
  children: React.ReactNode;
}

const FooterSection: FC<FooterSectionProps> = ({ title, children }) => (
  <div>
    <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">{title}</h3>
    {children}
  </div>
);

const socialLinks = [
  { name: 'Facebook', href: 'https://facebook.com', icon: Facebook },
  { name: 'Instagram', href: 'https://instagram.com', icon: Instagram },
  { name: 'Twitter', href: 'https://twitter.com', icon: Twitter },
];

const quickLinks = [
  { name: 'Home', to: '/' },
  { name: 'Services', to: '/services' },
  { name: 'Therapists', to: '/therapists' },
  { name: 'Book Now', to: '/book' },
  { name: 'Contact', to: '/contact' },
];

const serviceLinks = [
  { name: 'Swedish Massage', to: '/services/swedish-massage' },
  { name: 'Deep Tissue', to: '/services/deep-tissue' },
  { name: 'Hot Stone', to: '/services/hot-stone' },
  { name: 'Sports Massage', to: '/services/sports-massage' },
];

export interface FooterProps {
  className?: string;
}

export const Footer: FC<FooterProps> = ({ className }) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className={cn('bg-gray-900 text-gray-300', className)}>
      {/* Main Footer Content */}
      <Container className="py-12 lg:py-16">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {/* Brand/About Section */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <span className="text-2xl font-bold text-white">
                Massa<span className="text-teal-400">therapy</span>
              </span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-xs">
              Professional massage therapy services dedicated to your wellness and relaxation.
              Experience the healing power of therapeutic touch.
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'flex items-center justify-center w-10 h-10 rounded-full',
                    'bg-gray-800 text-gray-400 hover:bg-teal-500 hover:text-white',
                    'transition-all duration-200'
                  )}
                  aria-label={`Follow us on ${social.name}`}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <FooterSection title="Quick Links">
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <FooterLink to={link.to}>{link.name}</FooterLink>
                </li>
              ))}
            </ul>
          </FooterSection>

          {/* Services */}
          <FooterSection title="Our Services">
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.name}>
                  <FooterLink to={link.to}>{link.name}</FooterLink>
                </li>
              ))}
            </ul>
          </FooterSection>

          {/* Contact Info */}
          <FooterSection title="Contact Us">
            <address className="not-italic space-y-3 text-sm text-gray-400">
              <p className="flex flex-col">
                <span className="text-gray-400 text-xs uppercase tracking-wide mb-1">Address</span>
                <span className="text-gray-300">123 Wellness Street</span>
                <span className="text-gray-300">Suite 100</span>
                <span className="text-gray-300">San Francisco, CA 94102</span>
              </p>
              <p className="flex flex-col">
                <span className="text-gray-400 text-xs uppercase tracking-wide mb-1">Phone</span>
                <a href="tel:+14155551234" className="text-gray-300 hover:text-teal-400 transition-colors">
                  (415) 555-1234
                </a>
              </p>
              <p className="flex flex-col">
                <span className="text-gray-400 text-xs uppercase tracking-wide mb-1">Email</span>
                <a
                  href="mailto:info@massatherapy.com"
                  className="text-gray-300 hover:text-teal-400 transition-colors"
                >
                  hello@massatherapy.com
                </a>
              </p>
              <p className="flex flex-col">
                <span className="text-gray-400 text-xs uppercase tracking-wide mb-1">Hours</span>
                <span className="text-gray-300">Mon - Fri: 9am - 8pm</span>
                <span className="text-gray-300">Sat - Sun: 10am - 6pm</span>
              </p>
            </address>
          </FooterSection>
        </div>
      </Container>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <Container className="py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">
              &copy; {currentYear} Massatherapy. All rights reserved.
            </p>
            <nav className="flex items-center gap-6">
              <FooterLink to="/privacy">Privacy Policy</FooterLink>
              <FooterLink to="/terms">Terms of Service</FooterLink>
            </nav>
          </div>
        </Container>
      </div>
    </footer>
  );
};

Footer.displayName = 'Footer';
